<script lang="ts" setup>
import { watch } from 'vue'

import { MultiSelect, MultiSelectObjectOption } from '@collector/shared-ui'
import { BaseParticipant } from '@collector/sportsapi-client-legacy'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'
import { useEvent, useSport } from '@desktop/views/Relation/composables'

import { injectSuccessBadge } from '../../successBadge'
import { useSearchTermOptions } from '../composables'

type OptionValue = { id: number; name: string }

// inject
const { showSuccessBadge } = injectSuccessBadge()

// composables
const { sport } = useSport()
const { event } = useEvent()

const {
  selectedValue,
  selectedValueOptions,
  searchTerm,
  options,
  defaultOptions,
} = useSearchTermOptions(fetchSelectOptions)

// methods
async function fetchSelectOptions(
  searchTermValue?: string,
): Promise<MultiSelectObjectOption[]> {
  const referees = await sportsApiClient.Participants.get({
    sport_id: sport.value.id,
    subtype: 'referee',
    short_name: searchTermValue,
  })

  return (referees.api.data.participants || []).map(getOption)
}

function getOption(participant: BaseParticipant): MultiSelectObjectOption {
  return {
    name: participant.short_name,
    value: getOptionValue(participant),
    metadata: participant,
  }
}

function getOptionValue(participant: BaseParticipant): {
  id: number
  name: string
} {
  return {
    id: participant.id,
    name: participant.short_name,
  }
}

function getOptionDescription(participant: BaseParticipant): string {
  return `${participant.area_name} ${participant.details.birthdate ? `(${participant.details.birthdate})` : ''}`
}

async function updateEventReferee(
  newValue: OptionValue | undefined,
): Promise<void> {
  selectedValue.value = newValue
  const refereeId = newValue?.id ?? null

  await sportsApiClient.Events.patch(event.value.id, {
    event: { referee_id: refereeId },
  })

  // if value was cleared, reset options to default
  if (!newValue) {
    options.value = defaultOptions
  }

  showSuccessBadge()
}

async function setRefereeFromEvent(): Promise<void> {
  if (event.value.referee_id === null) {
    selectedValue.value = undefined
    return
  }

  // fetch current referee_id from API, create single option in multiselect, then select option
  const response = await sportsApiClient.Participants.getById(
    event.value.referee_id,
  )

  const referee = response.api.data.participant

  if (referee) {
    const refereeOptions = [getOption(referee)]

    options.value = refereeOptions
    selectedValueOptions.value = refereeOptions

    if (event.value.referee_id !== (selectedValue.value as OptionValue)?.id) {
      selectedValue.value = getOptionValue(referee)
    }
  }
}

// init
watch(() => event.value.referee_id, setRefereeFromEvent, { immediate: true })
</script>

<template>
  <MultiSelect
    v-model:searchTerm="searchTerm"
    :modelValue="selectedValue"
    withFilter
    withClear
    :options
    @update:modelValue="updateEventReferee($event as OptionValue)"
  >
    <span class="w-12">Referee</span>
    <template
      v-if="selectedValue"
      #displayedValue
    >
      {{ (selectedValue as OptionValue).name }}
    </template>
    <template #option="{ option }">
      <div class="leading-3">
        <div>
          {{ option.name }}
        </div>
        <div class="text-2xs">
          {{ getOptionDescription(option.metadata as BaseParticipant) }}
        </div>
      </div>
    </template>
  </MultiSelect>
</template>
